import * as React from 'react'
import { useAppDispatch } from '../../../../actions/types'
import { useAppSelector } from '../../../../reducers'
import { Channel } from '../../../../types'
import useContentURLParams from './useContentURLParams'
import { useSize } from '../../../../components/utils'

/**
 * This hooks keeps the logic for fetching channel close.
 * It returns the data for the selected channel and requests it if needed.
 * It also selects a default playlist when the selected channel data is loaded
 * @returns 
 */
export default function useSelectedChannel(): Channel | null {
    const dispatch = useAppDispatch()
    const selectedChannel = useAppSelector<Channel | null>(
        state => state.content.selected.channels,
    )
    const {
        navigateInContentPage,
        searchChannel,
        selectedPlaylistId,
    } = useContentURLParams()
    const { isMobile } = useSize()
    /**
     * Effect for navigating to the default playlist when a channel is selected
     * This behavior is disabled for mobiles
     */
    React.useEffect(() => {
        // If there's already a playlist selected, or selectedChannel is empty
        // don't do anything
        if (selectedPlaylistId || !selectedChannel || isMobile) {
            return
        }

        const { id: channelId, playlists = [] } = selectedChannel

        // Look for the first MAIN playlist
        let defaultPlaylist = playlists.find(
            playlist => playlist.slug === 'MAIN'
        )

        // If there's no MAIN playlist, take the first in the list
        if (!defaultPlaylist && playlists.length > 0) {
            defaultPlaylist = playlists[0]
        }

        // Add the playlist ID to the page's route
        navigateInContentPage({
            channel: channelId,
            playlist: defaultPlaylist?.id,
            search: searchChannel,
        })
    }, [
        dispatch,
        navigateInContentPage,
        searchChannel,
        selectedChannel,
        selectedPlaylistId,
        isMobile,
    ])

    return selectedChannel
}